<script lang="ts" setup>
import { computed, ref } from 'vue'

import { Result } from '@mobile/views/Relation/Shared/IncidentResult'
import HistoryDelete from '@mobile/views/Relation/Shared/Popups/History/HistoryItem/HistoryDelete.vue'
import HistoryDisplay from '@mobile/views/Relation/Shared/Popups/History/HistoryItem/HistoryDisplay.vue'
import HistoryEdit from '@mobile/views/Relation/Shared/Popups/History/HistoryItem/HistoryEdit.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

interface Props {
  linkedIncident: ProbableLinkedIncident
  result: Result | null
}

const props = defineProps<Props>()

enum Status {
  Display,
  Edit,
  Delete,
}

const edit = ref<Status>(Status.Display)

const additionalHeaderInfo = computed(() => {
  const additionalHeaderInfo = []
  const additionalInfo = props.linkedIncident.incident.additional_info ?? {}

  if (additionalInfo.current_over !== undefined) {
    additionalHeaderInfo.push({
      name: 'Current Over',
      value: additionalInfo.current_over,
    })
  }

  if (additionalInfo.current_ball !== undefined) {
    additionalHeaderInfo.push({
      name: 'Current Ball',
      value: additionalInfo.current_ball,
    })
  }

  return additionalHeaderInfo
})

function showView(status: Status): void {
  edit.value = status
}
</script>

<template>
  <div>
    <HistoryDisplay
      v-show="edit === Status.Display"
      :linkedIncident
      :result
      :additionalHeaderInfo
      @toggleEdit="showView(Status.Edit)"
      @toggleDelete="showView(Status.Delete)"
    />
    <HistoryEdit
      v-if="edit === Status.Edit"
      :linkedIncident
      @close="showView(Status.Display)"
    />
    <HistoryDelete
      v-show="edit === Status.Delete"
      :linkedIncident
      @close="showView(Status.Display)"
    />
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@mobile/styles/variables.scss' as vars;
@use '@mobile/styles/mixins.scss' as mixins;

.header-property {
  $timeWidth: 70px;
  $marginBetweenProperties: 6px;
  $countOfAllProperties: 4;
  $countOfDynamicProperties: 3;

  @mixin dynamicPropertyWidth($percentWidth) {
    $timeOffset: math.div($timeWidth, $countOfDynamicProperties);
    $marginOffset: math.div(
      $marginBetweenProperties * $countOfAllProperties * 2,
      $countOfDynamicProperties
    );

    @include mixins.staticWidth(
      calc(#{$percentWidth} - #{$timeOffset} - #{$marginOffset})
    );
  }

  display: flex;
  margin: 0 $marginBetweenProperties;
  overflow: hidden;
  font-size: 10px;
  white-space: nowrap;
  align-items: center;

  &-time {
    @include mixins.staticWidth($timeWidth);
  }

  &-status {
    @include dynamicPropertyWidth(24%);
  }

  &-participant {
    @include dynamicPropertyWidth(38%);
  }

  &-info {
    @include dynamicPropertyWidth(38%);
  }
}
</style>
